import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';

interface Plan {
  plan_type: string;
  seat_count: number;
  [key: string]: any;
}

interface Features {
  can_create_own_cases: boolean;
  can_add_team_members: boolean;
  has_team_dashboard: boolean;
  can_share_documents: boolean;
  has_case_chat: boolean;
  [key: string]: boolean;
}

interface PlanContextType {
  plan: Plan | null;
  features: Features | null;
  loading: boolean;
  error: string | null;
  canUseFeature: (featureName: string) => boolean;
  isPlanAtLeast: (requiredPlan: string) => boolean;
  upgradeUrl: string;
  refreshPlan: () => Promise<void>;
}

interface PlanProviderProps {
  children: ReactNode;
}

const PlanContext = createContext<PlanContextType>({} as PlanContextType);

/**
 * Plan Context Provider
 * Manages subscription plan state and feature access across the app
 */
export const PlanProvider: React.FC<PlanProviderProps> = ({ children }) => {
  const [plan, setPlan] = useState<Plan | null>(null);
  const [features, setFeatures] = useState<Features | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  const fetchCurrentPlan = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const user = localStorage.getItem('user');
      
      // Only fetch if user is logged in (has both token AND user data)
      if (!token || !user) {
        // No auth, default to FREE (for public pages)
        setPlan({ plan_type: 'FREE', seat_count: 1 });
        setFeatures({
          can_create_own_cases: false,
          can_add_team_members: false,
          has_team_dashboard: false,
          can_share_documents: false,
          has_case_chat: false
        });
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API_URL}/api/subscriptions/current`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setPlan(response.data.tenant);
      setFeatures(response.data.features);
      setError(null);
    } catch (err: any) {
      console.error('Failed to fetch plan:', err);
      setError(err.message);
      
      // Fallback to FREE on error
      setPlan({ plan_type: 'FREE', seat_count: 1 });
      setFeatures({
        can_create_own_cases: false,
        can_add_team_members: false,
        has_team_dashboard: false,
        can_share_documents: false,
        has_case_chat: false
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCurrentPlan();
  }, []);

  const canUseFeature = (featureName: string): boolean => {
    if (!features) return false;
    return features[featureName] === true;
  };

  const isPlanAtLeast = (requiredPlan: string): boolean => {
    if (!plan) return false;
    
    const planHierarchy = ['FREE', 'PRO', 'TEAM', 'ENTERPRISE'];
    const currentIndex = planHierarchy.indexOf(plan.plan_type);
    const requiredIndex = planHierarchy.indexOf(requiredPlan);
    
    return currentIndex >= requiredIndex;
  };

  const upgradeUrl = '/settings/billing';

  const value: PlanContextType = {
    plan,
    features,
    loading,
    error,
    canUseFeature,
    isPlanAtLeast,
    upgradeUrl,
    refreshPlan: fetchCurrentPlan
  };

  return (
    <PlanContext.Provider value={value}>
      {children}
    </PlanContext.Provider>
  );
};

/**
 * Hook to use plan context
 * 
 * Usage:
 *   const { plan, canUseFeature, upgradeUrl } = usePlan();
 *   
 *   if (canUseFeature('can_add_team_members')) {
 *     // Show team invite button
 *   }
 */
export const usePlan = (): PlanContextType => {
  const context = useContext(PlanContext);
  
  if (!context) {
    throw new Error('usePlan must be used within PlanProvider');
  }
  
  return context;
};

export default PlanContext;
